import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Barbell, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_AVATAR_BYTES = 800 * 1024;
const readFileAsDataURL = (file) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(fr.result);
  fr.onerror = rej;
  fr.readAsDataURL(file);
});

export default function BecomeInstructor() {
  const { user, ready, addRole, setActiveRole, formatApiError } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    bio: "",
    phone: "",
    address: "",
    date_of_birth: "",
    avatar_url: "",
    attestation_accepted: false,
  });
  const [avatarPreview, setAvatarPreview] = useState("");
  const [loading, setLoading] = useState(false);

  // Guardrails: user must be logged in AND not already an instructor
  useEffect(() => {
    if (!ready) return;
    if (!user) { navigate("/login?redirect=/become-instructor", { replace: true }); return; }
    const roles = user.roles || (user.role ? [user.role] : []);
    if (roles.includes("instructor")) navigate("/instructor", { replace: true });
    // Prefill phone if already known
    if (user.phone) setForm((f) => ({ ...f, phone: user.phone }));
    if (user.bio) setForm((f) => ({ ...f, bio: user.bio }));
  }, [ready, user, navigate]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_AVATAR_BYTES) { toast.error(t("auth.photo_too_large")); return; }
    if (!file.type.startsWith("image/")) { toast.error(t("auth.photo_invalid_type")); return; }
    const dataUrl = await readFileAsDataURL(file);
    setForm((f) => ({ ...f, avatar_url: dataUrl }));
    setAvatarPreview(dataUrl);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bio.trim()) { toast.error(t("become.bio_required")); return; }
    if (!form.address.trim()) { toast.error(t("auth.address_required")); return; }
    if (!form.date_of_birth) { toast.error(t("auth.dob_required")); return; }
    if (!form.avatar_url) { toast.error(t("auth.photo_required")); return; }
    if (!form.attestation_accepted) { toast.error(t("auth.attestation_required")); return; }
    setLoading(true);
    try {
      await addRole({
        role: "instructor",
        bio: form.bio.trim(),
        phone: form.phone.trim() || undefined,
        address: form.address.trim(),
        date_of_birth: form.date_of_birth,
        avatar_url: form.avatar_url,
        attestation_accepted: true,
      });
      await setActiveRole("instructor");
      toast.success(t("become.success"));
      navigate("/instructor");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!ready || !user) return null;

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-16" data-testid="become-instructor-page">
      <div className="max-w-2xl mx-auto">
        <div className="border border-white/10 bg-[#0A0A0A] p-8">
          <div className="flex items-center gap-3">
            <Barbell size={28} weight="duotone" className="text-[#E30613]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#E30613] font-bold">{t("become.tag")}</div>
              <h1 className="font-display text-3xl uppercase font-black mt-1">{t("become.title")}</h1>
            </div>
          </div>
          <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{t("become.description")}</p>

          {/* Reassuring header: existing account is kept */}
          <div className="mt-4 border border-[#00FF66]/30 bg-[#00FF66]/5 px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-[#00FF66] font-bold">
              {t("become.keeps_account_tag")}
            </div>
            <div className="text-xs text-zinc-300 mt-1">
              {t("become.keeps_account_body", { email: user.email })}
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.bio")}</Label>
              <Textarea rows={4} required value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder={t("auth.bio_placeholder")}
                className="bg-black border-white/20 rounded-none focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                data-testid="become-bio-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("auth.phone_placeholder")}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                data-testid="become-phone-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.date_of_birth")}</Label>
              <Input type="date" required value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                data-testid="become-dob-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.address")}</Label>
              <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder={t("auth.address_placeholder")}
                className="bg-black border-white/20 rounded-none h-11 focus-visible:ring-[#E30613] focus-visible:border-[#E30613]"
                data-testid="become-address-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-zinc-400">{t("auth.photo")}</Label>
              <div className="flex items-center gap-4">
                <label htmlFor="become-avatar-upload" className="cursor-pointer inline-block bg-white/5 hover:bg-white/10 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest font-bold text-white">
                  {form.avatar_url ? t("auth.photo_change") : t("auth.photo_choose")}
                </label>
                <input id="become-avatar-upload" type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="become-photo-input" />
                {avatarPreview && (
                  <img src={avatarPreview} alt="preview" className="w-16 h-16 object-cover border border-white/20" />
                )}
              </div>
              <div className="text-[10px] text-zinc-500">{t("auth.photo_hint")}</div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer p-3 border border-white/10 bg-black" data-testid="become-attestation-label">
              <input type="checkbox" required checked={form.attestation_accepted}
                onChange={(e) => setForm({ ...form, attestation_accepted: e.target.checked })}
                className="mt-1 shrink-0" data-testid="become-attestation" />
              <span className="text-xs text-zinc-300 leading-relaxed">{t("auth.attestation")}</span>
            </label>

            <div className="text-[10px] text-zinc-500 uppercase tracking-wider border-l-2 border-[#E30613] pl-3 py-1">
              {t("auth.instructor_note")}
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={loading}
                className="bg-[#E30613] hover:bg-[#FF1A27] text-white uppercase tracking-[0.2em] font-bold rounded-none h-11 px-6"
                data-testid="become-submit-button">
                {loading ? t("common.loading") : t("become.submit")} <ArrowRight size={14} className="ml-2" weight="bold" />
              </Button>
              <Link to="/">
                <Button type="button" variant="ghost" className="text-xs uppercase tracking-wider font-bold rounded-none h-11">{t("common.cancel")}</Button>
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
